/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { useCallback, useEffect, useState } from 'react';

import { COMMAND_INTELLIGENCE_CONTENT_SELECTOR } from './drawerLayout';

const getViewportInsets = () => {
  if (typeof window === 'undefined') {
    return { top: 0, left: 0, right: 0, bottom: 0, width: 0, height: 0 };
  }

  const width = window.innerWidth;
  const height = window.innerHeight;

  return {
    top: 0,
    left: 0,
    right: width,
    bottom: height,
    width,
    height,
  };
};

const readContentInsets = () => {
  if (typeof document === 'undefined') {
    return getViewportInsets();
  }

  const contentEl = document.querySelector(COMMAND_INTELLIGENCE_CONTENT_SELECTOR);

  if (!contentEl) {
    return getViewportInsets();
  }

  const rect = contentEl.getBoundingClientRect();

  return {
    top: rect.top,
    left: rect.left,
    right: rect.right,
    bottom: rect.bottom,
    width: rect.width,
    height: rect.height,
  };
};

export const useCommandIntelligenceContentInsets = () => {
  const [insets, setInsets] = useState(() => readContentInsets());

  const updateInsets = useCallback(() => {
    setInsets(readContentInsets());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let resizeObserver;
    let mutationObserver;
    let observedContentEl = null;

    const getContentEl = () =>
      typeof document !== 'undefined' ? document.querySelector(COMMAND_INTELLIGENCE_CONTENT_SELECTOR) : null;

    const syncContentObserver = () => {
      const contentEl = getContentEl();

      if (contentEl === observedContentEl) {
        return;
      }

      resizeObserver?.disconnect?.();
      observedContentEl = contentEl;

      if (contentEl && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(updateInsets);
        resizeObserver.observe(contentEl);
      }

      updateInsets();
    };

    syncContentObserver();

    const mutationTarget =
      typeof document !== 'undefined' ? document.querySelector('.main-container') ?? document.body : null;

    if (mutationTarget && typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(syncContentObserver);
      mutationObserver.observe(mutationTarget, { childList: true, subtree: true });
    }

    window.addEventListener('resize', updateInsets);

    return () => {
      resizeObserver?.disconnect?.();
      mutationObserver?.disconnect?.();
      window.removeEventListener('resize', updateInsets);
    };
  }, [updateInsets]);

  return insets;
};
